/* Reusable cell renderers for client profile tables. */
import type { ComponentProps, FC, ReactNode } from "react";
import { Check, MoreHorizontal, type LucideIcon } from "lucide-react";

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { StatusBadge, type StatusTone } from "@/components/ui/status-badge";
import { TableCell, TableRow } from "@/components/ui/table";
import { cn } from "@/lib/utils";

import { formatDayLabel, dateRangeLabel } from "../../lib/records";
import {
  KitIcon,
  KitMenu,
  type KitIconName,
  type KitMenuItem
} from "./KitMenu";
import { TABLE_TEXT } from "./ClientTable";

export type BadgeVariant =
  | "brand"
  | "neutral"
  | "error"
  | "warning"
  | "success";

const BADGE_TONE: Record<BadgeVariant, StatusTone> = {
  brand: "accent",
  neutral: "neutral",
  error: "danger",
  warning: "warn",
  success: "success"
};

export const initialsOf = (label: string): string => {
  const parts = label.trim().split(/\s+/).filter(Boolean);
  const first = parts[0]?.[0] ?? "";
  const last = parts.length > 1 ? parts[parts.length - 1]?.[0] ?? "" : "";
  return `${first}${last}`.toUpperCase() || "?";
};

/**
 * A 48px table cell. `className` styles the inner flex row (alignment, gap,
 * padding); every other prop goes on the `<td>`.
 */
export const ClientTableCell: FC<ComponentProps<"td">> = ({
  className,
  children,
  ...props
}) => (
  <TableCell className="h-auto p-0 whitespace-normal" {...props}>
    <div className={cn("flex h-12 w-full items-center gap-1 px-3", className)}>
      {children}
    </div>
  </TableCell>
);

/** Title + optional subtitle, with an optional avatar or lucide icon. */
export const TitleCell: FC<{
  title: ReactNode;
  subtitle?: ReactNode;
  icon?: LucideIcon;
  iconClassName?: string;
  /** Replaces the lucide icon slot (for example a file-type mark). */
  leading?: ReactNode;
  /** `variant` is accepted for compatibility; avatars use the neutral fill. */
  avatar?: { image?: string | null; label: string; variant?: BadgeVariant };
  trailing?: ReactNode;
  className?: string;
}> = ({
  title,
  subtitle,
  icon: Icon,
  iconClassName,
  leading,
  avatar,
  trailing,
  className
}) => (
  <ClientTableCell className={cn("gap-3", className)}>
    {avatar ? (
      <Avatar size="sm">
        {avatar.image ? <AvatarImage src={avatar.image} alt="" /> : null}
        <AvatarFallback>{initialsOf(avatar.label)}</AvatarFallback>
      </Avatar>
    ) : null}
    {leading ? (
      <span className="flex size-8 shrink-0 items-center justify-center">
        {leading}
      </span>
    ) : Icon ? (
      <span
        className={cn(
          "flex size-8 shrink-0 items-center justify-center rounded-md bg-fill-1 text-fg-3",
          iconClassName
        )}
      >
        <Icon className="size-4" aria-hidden />
      </span>
    ) : null}
    <div className="flex min-w-0 flex-1 flex-col">
      <span className={cn("truncate", TABLE_TEXT.primary)}>{title}</span>
      {subtitle ? (
        <span className={cn("truncate", TABLE_TEXT.muted)}>{subtitle}</span>
      ) : null}
    </div>
    {trailing}
  </ClientTableCell>
);

/** Secondary text cell. */
export const MutedCell: FC<{
  children?: ReactNode;
  className?: string;
  nowrap?: boolean;
}> = ({ children, className, nowrap = true }) => (
  <ClientTableCell className={className}>
    <span className={cn(TABLE_TEXT.muted, nowrap ? "whitespace-nowrap" : "")}>
      {children ?? "—"}
    </span>
  </ClientTableCell>
);

/** Primary body text cell. */
export const TextCell: FC<{ children?: ReactNode; className?: string }> = ({
  children,
  className
}) => (
  <ClientTableCell className={className}>
    <span className={cn("truncate", TABLE_TEXT.body)}>{children ?? "—"}</span>
  </ClientTableCell>
);

/** "Mar 4, 2024" or "—". */
export const DateCell: FC<{ value?: string | null; className?: string }> = ({
  value,
  className
}) => <MutedCell className={className}>{formatDayLabel(value)}</MutedCell>;

/** "Mar 2024 – Present". */
export const DateRangeCell: FC<{
  start?: string | null;
  end?: string | null;
  isCurrent?: boolean;
  className?: string;
}> = ({ start, end, isCurrent, className }) => (
  <MutedCell className={className}>
    {dateRangeLabel(start, end, isCurrent)}
  </MutedCell>
);

export const BadgeCell: FC<{
  label?: ReactNode;
  variant?: BadgeVariant;
  icon?: KitIconName;
  className?: string;
}> = ({ label, variant = "neutral", icon, className }) => (
  <ClientTableCell className={className}>
    {label ? (
      <StatusBadge tone={BADGE_TONE[variant]}>
        <KitIcon name={icon} />
        {label}
      </StatusBadge>
    ) : (
      <span className={TABLE_TEXT.muted}>—</span>
    )}
  </ClientTableCell>
);

/** Check mark for boolean columns such as "Current". */
export const CheckCell: FC<{
  checked: boolean;
  label?: string;
  className?: string;
}> = ({ checked, label = "Current", className }) => (
  <ClientTableCell className={className}>
    {checked ? (
      <StatusBadge tone="success">
        <Check aria-hidden />
        {label}
      </StatusBadge>
    ) : (
      <span className={TABLE_TEXT.muted}>—</span>
    )}
  </ClientTableCell>
);

/** Trailing "…" menu. Clicks never reach the row. */
export const RowMenuCell: FC<{
  items: KitMenuItem[];
  ariaLabel?: string;
  className?: string;
}> = ({ items, ariaLabel = "Row actions", className }) => (
  <ClientTableCell className={cn("justify-end", className)}>
    <span
      data-row-stop
      onClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => e.stopPropagation()}
      role="presentation"
    >
      <KitMenu
        align="end"
        stopRowClick
        trigger={
          <Button variant="ghost" size="icon-sm" aria-label={ariaLabel}>
            <MoreHorizontal />
          </Button>
        }
        items={items}
      />
    </span>
  </ClientTableCell>
);

/** Standard Edit / Delete row menu items. */
export const editDeleteItems = (
  onEdit: () => void,
  onDelete: () => void,
  deleteLabel = "Delete"
): KitMenuItem[] => [
  { key: "edit", label: "Edit", icon: "FeatherEdit2", onSelect: onEdit },
  {
    key: "delete",
    label: deleteLabel,
    icon: "FeatherTrash2",
    destructive: true,
    onSelect: onDelete
  }
];

/** Skeleton row: optional leading circle, then one text skeleton per width. */
export const SkeletonRow: FC<{ widths: string[]; avatar?: boolean }> = ({
  widths,
  avatar = false
}) => (
  <TableRow className="hover:bg-transparent">
    {widths.map((w, i) => (
      <ClientTableCell
        key={i}
        className={i === 0 && avatar ? "gap-3" : undefined}
      >
        {i === 0 && avatar ? <Skeleton className="size-6 rounded-md" /> : null}
        <Skeleton className={cn("h-3", w)} />
      </ClientTableCell>
    ))}
  </TableRow>
);
